import React from 'react';
import { hero, apps, testimonial, closingCta, PCOServicesIcon, ProductHuntBadge } from './content';
import { usePricing } from './usePricing';

const SHELL = 'bg-stone-950 text-stone-100';
const ACCENT = 'text-lime-300';
const ACCENT_BG = 'bg-lime-300';
const SUBDUED = 'text-stone-400';
const RING = 'ring-stone-800';
const PANEL = 'bg-stone-900';

function PrimaryButton({ href, children, onClick, size = 'md' }) {
  const sizing = size === 'lg' ? 'py-3.5 px-7 text-base' : 'py-3 px-6 text-sm';
  return (
    <a
      href={href}
      onClick={onClick}
      className={`inline-flex items-center justify-center rounded-md ${ACCENT_BG} ${sizing} font-semibold text-stone-950 hover:bg-lime-200 active:bg-lime-400 transition-colors`}
    >
      {children}
    </a>
  );
}

function PlainButton({ href, children, onClick }) {
  return (
    <a
      href={href}
      onClick={onClick}
      className="inline-flex items-center justify-center py-3 px-4 text-sm font-semibold text-stone-100 hover:text-white transition-colors"
    >
      {children}
    </a>
  );
}

function ScreenshotFrame({ children }) {
  return (
    <div className={`relative rounded-lg p-2 ${PANEL} ring-1 ${RING} shadow-2xl shadow-black/50`}>
      <div className="overflow-hidden rounded-md">{children}</div>
    </div>
  );
}

function FeatureMedia({ media }) {
  if (media.type === 'icon') {
    return (
      <div className={`flex h-full w-full items-center justify-center rounded-lg ${PANEL} ring-1 ${RING} p-12`}>
        <div className="flex flex-col items-center gap-4">
          <PCOServicesIcon className="h-32 w-32" />
          <p className="text-sm font-semibold text-stone-200">Planning Center Services</p>
        </div>
      </div>
    );
  }
  return (
    <ScreenshotFrame>
      <img src={media.src} alt={media.alt} className="block w-full h-auto" loading="lazy" />
    </ScreenshotFrame>
  );
}

function FeatureCard({ feature }) {
  return (
    <div className={`rounded-xl ${PANEL} ring-1 ${RING} p-6 flex flex-col`}>
      <div className="mb-6">
        <FeatureMedia media={feature.media} />
      </div>
      <h3 className="text-xl font-semibold tracking-tight text-stone-50">{feature.title}</h3>
      <p className="mt-3 text-base text-stone-400 leading-relaxed">{feature.body}</p>
    </div>
  );
}

function AppSection({ app, anchor }) {
  return (
    <section id={anchor} className="py-20 sm:py-28 border-t border-stone-900">
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-2xl mb-14">
          <p className={`text-sm font-semibold uppercase tracking-wide ${ACCENT}`}>{app.name}</p>
          <h2 className="mt-3 text-3xl sm:text-5xl font-semibold tracking-tight text-stone-50 leading-tight">
            {app.tagline}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {app.features.map((feature) => (
            <FeatureCard key={feature.title} feature={feature} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingSection({ onCtaClick }) {
  const { loading, error, byApp } = usePricing();
  const renderApp = (appKey, label) => {
    const products = byApp[appKey] || [];
    return (
      <div key={appKey} className={`rounded-xl ${PANEL} ring-1 ${RING} p-8 flex flex-col`}>
        <p className={`text-sm font-semibold uppercase tracking-wide ${ACCENT}`}>{label}</p>
        {products.length === 0 ? (
          <p className="mt-6 text-stone-400 text-sm">
            {loading ? 'Loading pricing…' : (error ? 'Pricing temporarily unavailable.' : 'Plans coming soon.')}
          </p>
        ) : (
          <div className="mt-6 space-y-6 flex-1">
            {products.map((product) => (
              <div key={product.id}>
                <p className="text-lg font-semibold text-stone-50">{product.name}</p>
                {product.description && <p className="mt-1 text-sm text-stone-400">{product.description}</p>}
                <ul className="mt-3 divide-y divide-stone-800">
                  {product.tiers.map((tier) => (
                    <li key={tier.id} className="flex items-baseline justify-between py-2 text-sm">
                      <span className="text-stone-400">{tier.label || 'Plan'}</span>
                      <span className="font-semibold text-stone-50">{tier.formatted}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
        <div className="mt-8">
          <PrimaryButton href={hero.primaryCta.href} onClick={() => onCtaClick && onCtaClick(`pricing_${appKey}`)}>
            {hero.primaryCta.text}
          </PrimaryButton>
        </div>
      </div>
    );
  };
  return (
    <section id="pricing" className="py-20 sm:py-28 border-t border-stone-900">
      <div className="mx-auto max-w-6xl px-6">
        <div className="max-w-2xl mb-14">
          <p className={`text-sm font-semibold uppercase tracking-wide ${ACCENT}`}>Pricing</p>
          <h2 className="mt-3 text-3xl sm:text-5xl font-semibold tracking-tight text-stone-50">
            Pay for what you use.
          </h2>
          <p className={`mt-4 text-lg ${SUBDUED}`}>Subscribe to one app, or both. Cancel anytime.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {renderApp('slides', 'Slides')}
          {renderApp('billboard', 'Billboard')}
        </div>
      </div>
    </section>
  );
}

function PullQuote({ quote }) {
  if (!quote) return null;
  return (
    <section className="py-20 sm:py-24 border-t border-stone-900">
      <div className="mx-auto max-w-3xl px-6 text-center">
        <p className={`text-3xl sm:text-4xl font-medium tracking-tight text-stone-50 leading-snug`}>
          &ldquo;{quote.quote}&rdquo;
        </p>
        {(quote.name || quote.byline) && (
          <p className={`mt-6 text-sm font-semibold ${ACCENT}`}>
            {quote.name}{quote.byline ? ` · ${quote.byline}` : ''}
          </p>
        )}
      </div>
    </section>
  );
}

export default function HomeTreatment({ onCtaClick = () => {}, links = [] }) {
  return (
    <div className={SHELL}>
      <header className="mx-auto max-w-7xl px-6 py-6 flex items-center justify-between">
        <a href="/" className="flex items-center">
          <img src="/wk_logo_accounts.png" alt="WorshipKit" className="h-8 w-auto invert" />
        </a>
        <nav className="flex items-center gap-1 sm:gap-2">
          {links.map((link, idx) => {
            const isLast = idx === links.length - 1;
            return isLast ? (
              <PrimaryButton key={link.url} href={link.url} onClick={() => onCtaClick(`nav_${link.text.toLowerCase().replace(/\s+/g, '_')}`)}>
                {link.text}
              </PrimaryButton>
            ) : (
              <PlainButton key={link.url} href={link.url}>{link.text}</PlainButton>
            );
          })}
        </nav>
      </header>

      <section className="pt-10 pb-20 sm:pt-16 sm:pb-28">
        <div className="mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <h1 className="text-4xl sm:text-6xl font-semibold tracking-tight text-stone-50 leading-[1.05]">
              {hero.headline}
            </h1>
            <p className={`mt-6 text-xl ${SUBDUED} leading-relaxed`}>{hero.subhead}</p>
            <p className="mt-4 text-base text-stone-500 leading-relaxed">{hero.preamble}</p>
            <div className="mt-10 flex items-center gap-4">
              <PrimaryButton href={hero.primaryCta.href} onClick={() => onCtaClick('hero_primary')} size="lg">
                {hero.primaryCta.text}
              </PrimaryButton>
              <PlainButton href={hero.secondaryCta.href} onClick={() => onCtaClick('hero_secondary')}>
                {hero.secondaryCta.text} →
              </PlainButton>
            </div>
          </div>
          <div className="lg:col-span-6">
            <ScreenshotFrame>
              <img src={hero.demoImage} alt="WorshipKit Slides document review" className="block w-full h-auto" loading="lazy" />
            </ScreenshotFrame>
          </div>
        </div>
      </section>

      {apps.map((app) => (
        <AppSection key={app.key} app={app} anchor={app.key} />
      ))}

      <PullQuote quote={testimonial} />

      <PricingSection onCtaClick={onCtaClick} />

      <section className="py-20 sm:py-28 border-t border-stone-900">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight text-stone-50">{closingCta.headline}</h2>
          <p className={`mt-4 text-lg ${SUBDUED}`}>{closingCta.body}</p>
          <div className="mt-10 flex justify-center">
            <PrimaryButton href={closingCta.primaryCta.href} onClick={() => onCtaClick('footer_primary')} size="lg">
              {closingCta.primaryCta.text}
            </PrimaryButton>
          </div>
        </div>
      </section>

      <section className="border-t border-stone-900 py-10">
        <div className="mx-auto max-w-7xl px-6 flex flex-col items-center gap-6">
          <nav aria-label="Footer" className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            <a href="/help" className={`text-sm ${SUBDUED} hover:text-stone-200`}>
              Help
            </a>
            <a href="/vs/proclaim" className={`text-sm ${SUBDUED} hover:text-stone-200`}>
              vs Proclaim
            </a>
            <a href="/vs/easyworship" className={`text-sm ${SUBDUED} hover:text-stone-200`}>
              vs EasyWorship
            </a>
          </nav>
          <ProductHuntBadge theme="dark" />
        </div>
      </section>
    </div>
  );
}
